import { useEffect, useRef, useState } from "react";

/* --------------------------------------------------------------------------
   Constants
   -------------------------------------------------------------------------- */

const EMAILJS_SERVICE_ID = "service_phjsyq5";
const EMAILJS_TEMPLATE_ID = "template_0tyg2jk";
const EMAILJS_PUBLIC_KEY = "bRmPXALtqcHX4nz4a";
const CALENDLY_LINK = "https://calendly.com/sarisater/new-meeting";
const API_URL = "/api/analyze";

const SCAN_LINES = [
  "Fetching the homepage source.",
  "Mapping objections across the Big 5: trust, price, fit, timing, effort.",
  "Auditing persuasion assets: credentials, social proof, risk reversal, data specificity.",
  "Scanning the primary conversion action against competing CTAs.",
  "Locating unaddressed objections at points of friction.",
  "Estimating the revenue impact of the highest-leverage gap.",
  "Composing the audit.",
];

/* --------------------------------------------------------------------------
   Email HTML — dark navy + white theme
   -------------------------------------------------------------------------- */

const EMAIL_COLORS = {
  page:         "#f1f5f9",
  headerBg:     "#0d1b2a",
  headerMute:   "#64748b",
  headerBody:   "#94a3b8",
  card:         "#ffffff",
  ink:          "#111827",
  body:         "#374151",
  mute:         "#9ca3af",
  ruleSoft:     "#e5e7eb",
  accent:       "#f59e0b",
  ok:           "#059669",
  okBg:         "#ecfdf5",
  okBorder:     "#a7f3d0",
  warn:         "#d97706",
  warnBg:       "#fffbeb",
  warnBorder:   "#fde68a",
  bad:          "#dc2626",
  badBg:        "#fff1f2",
  badBorder:    "#fecaca",
  topWinBg:     "#eff6ff",
  topWinAccent: "#0284c7",
  topWinImpact: "#16a34a",
};

function scoreColor(s) {
  if (s >= 75) return EMAIL_COLORS.ok;
  if (s >= 50) return EMAIL_COLORS.warn;
  return EMAIL_COLORS.bad;
}

function scoreLabel(s) {
  if (s >= 75) return "Strong";
  if (s >= 50) return "Fair";
  if (s >= 30) return "Weak";
  return "Critical";
}

const findingTypeConfig = {
  critical:    { icon: "⚠",  color: EMAIL_COLORS.bad,  bg: EMAIL_COLORS.badBg,  border: EMAIL_COLORS.badBorder },
  warning:     { icon: "⚠",  color: EMAIL_COLORS.warn, bg: EMAIL_COLORS.warnBg, border: EMAIL_COLORS.warnBorder },
  opportunity: { icon: "▲",  color: EMAIL_COLORS.ok,   bg: EMAIL_COLORS.okBg,   border: EMAIL_COLORS.okBorder },
};

function buildEmailHTML(results, url, recipientEmail) {
  const domain = (url || "").replace(/^https?:\/\//, "").split("/")[0] || "your site";
  const c = EMAIL_COLORS;

  const scoreRow = (label, score) => {
    const color = scoreColor(score);
    const labelText = scoreLabel(score);
    const badgeBg = score >= 75 ? c.okBg : score >= 50 ? c.warnBg : c.badBg;
    return `
      <tr>
        <td style="padding:14px 0; border-bottom:1px solid ${c.ruleSoft};">
          <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
            <tr>
              <td style="font-family:'Helvetica Neue',Arial,sans-serif; font-size:14px; color:${c.ink}; padding:0 8px 0 0;">
                ${label}
              </td>
              <td style="text-align:right; padding:0 12px 0 0; white-space:nowrap;">
                <span style="font-family:'Helvetica Neue',Arial,sans-serif; font-size:11px; color:${color}; background:${badgeBg}; padding:2px 8px; border-radius:999px; font-weight:600;">${labelText}</span>
              </td>
              <td style="font-family:'Helvetica Neue',Arial,sans-serif; font-size:14px; color:${c.ink}; text-align:right; width:56px; font-variant-numeric:tabular-nums; font-weight:700; white-space:nowrap;">
                ${score}<span style="font-size:11px; color:${c.mute}; font-weight:400;">/100</span>
              </td>
            </tr>
            <tr>
              <td colspan="3" style="padding-top:8px;">
                <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                  <tr>
                    <td style="height:3px; line-height:3px; font-size:0; background:${color}; width:${score}%;">&nbsp;</td>
                    <td style="height:3px; line-height:3px; font-size:0; background:${c.ruleSoft}; width:${100 - score}%;">&nbsp;</td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </td>
      </tr>`;
  };

  const findingRow = (f) => {
    const cfg = findingTypeConfig[f.type] || findingTypeConfig.warning;
    return `
      <tr>
        <td style="padding:0 0 10px;">
          <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
            <tr>
              <td style="background:${cfg.bg}; border-left:3px solid ${cfg.border}; border-radius:6px; padding:13px 16px;">
                <p style="margin:0; font-family:'Helvetica Neue',Arial,sans-serif; font-size:14px; line-height:1.6; color:${c.ink};">
                  <span style="color:${cfg.color}; margin-right:6px;">${cfg.icon}</span>${f.text}
                </p>
              </td>
            </tr>
          </table>
        </td>
      </tr>`;
  };

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <meta name="color-scheme" content="light only" />
  <meta name="supported-color-schemes" content="light" />
  <title>Growth Audit, ${domain}</title>
  <style>
    :root { color-scheme: light only; }
    body { color-scheme: light only; background-color: ${c.page} !important; }
  </style>
</head>
<body style="margin:0; padding:0; background:${c.page}; font-family:'Helvetica Neue', Arial, sans-serif; color-scheme:light;">
  <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="background:${c.page}; padding:32px 12px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" role="presentation" style="max-width:600px; width:100%; border-radius:16px; overflow:hidden; border:1px solid #e2e8f0;">

          <!-- HEADER -->
          <tr>
            <td style="background:${c.headerBg}; padding:32px 40px 0;">
              <p style="margin:0 0 20px; font-family:'Courier New',monospace; font-size:11px; letter-spacing:0.18em; text-transform:uppercase; color:${c.headerMute};">
                YourGrowthPartner.io
              </p>
              <h1 style="margin:0 0 6px; font-family:'Helvetica Neue',Arial,sans-serif; font-size:28px; line-height:1.1; font-weight:700; color:#ffffff;">
                Your Free Growth &amp; CRO Audit
              </h1>
              <p style="margin:0 0 28px; font-family:'Helvetica Neue',Arial,sans-serif; font-size:14px; color:${c.headerMute};">
                ${domain}
              </p>
            </td>
          </tr>

          <!-- SCORE HERO -->
          <tr>
            <td style="background:${c.headerBg}; padding:0 40px 32px; border-bottom:2px solid #1e3a52;">
              <p style="margin:0 0 6px; font-family:'Courier New',monospace; font-size:11px; letter-spacing:0.18em; text-transform:uppercase; color:${c.headerBody};">
                Overall growth score
              </p>
              <p style="margin:0 0 18px; font-family:'Helvetica Neue',Arial,sans-serif; font-size:56px; line-height:1; font-variant-numeric:tabular-nums; font-weight:700; color:${c.accent};">
                ${results.overallScore}<span style="font-size:16px; color:${c.headerBody}; font-weight:400; margin-left:6px;">/100</span>
              </p>
              <p style="margin:0; font-family:'Helvetica Neue',Arial,sans-serif; font-size:15px; line-height:1.6; color:${c.headerBody};">
                ${results.summary || ""}
              </p>
            </td>
          </tr>

          <!-- SCORE BREAKDOWN -->
          <tr>
            <td style="background:${c.card}; padding:28px 40px 8px;">
              <p style="margin:0 0 16px; font-family:'Courier New',monospace; font-size:11px; letter-spacing:0.18em; text-transform:uppercase; color:${c.mute};">
                Score breakdown
              </p>
              <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                ${scoreRow("Conversion Rate Optimization", results.croScore)}
                ${scoreRow("Growth Gap Score", results.growthGapScore)}
                ${scoreRow("Messaging Clarity", results.messagingScore)}
                ${scoreRow("Trust &amp; Social Proof", results.trustScore)}
                ${scoreRow("CTA Effectiveness", results.ctaScore)}
              </table>
            </td>
          </tr>

          <!-- FINDINGS -->
          <tr>
            <td style="background:${c.card}; padding:24px 40px 20px;">
              <p style="margin:0 0 16px; font-family:'Courier New',monospace; font-size:11px; letter-spacing:0.18em; text-transform:uppercase; color:${c.mute};">
                Key findings
              </p>
              <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                ${(results.findings || []).map(findingRow).join("")}
              </table>
            </td>
          </tr>

          <!-- TOP WIN -->
          <tr>
            <td style="background:${c.card}; padding:0 40px 28px;">
              <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                <tr>
                  <td style="background:${c.topWinBg}; border-radius:8px; padding:20px 24px;">
                    <p style="margin:0 0 10px; font-family:'Courier New',monospace; font-size:11px; letter-spacing:0.18em; text-transform:uppercase; color:${c.topWinAccent}; font-weight:bold;">
                      &#9889; Your highest-impact win
                    </p>
                    <p style="margin:0 0 12px; font-family:'Helvetica Neue',Arial,sans-serif; font-size:16px; line-height:1.5; color:${c.ink}; font-weight:600;">
                      ${results.topWin || ""}
                    </p>
                    <p style="margin:0; font-family:'Helvetica Neue',Arial,sans-serif; font-size:13px; color:${c.topWinImpact}; font-weight:600;">
                      Estimated impact: ${results.estimatedRevenueImpact || ""}
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- CTA -->
          <tr>
            <td style="background:${c.headerBg}; padding:36px 40px 40px; text-align:center;">
              <h2 style="margin:0 0 12px; font-family:'Helvetica Neue',Arial,sans-serif; font-size:22px; line-height:1.3; font-weight:700; color:#ffffff;">
                Ready to close these gaps<br>in the next 90 days?
              </h2>
              <p style="margin:0 0 24px; font-family:'Helvetica Neue',Arial,sans-serif; font-size:14px; line-height:1.6; color:${c.headerBody};">
                Book a free 30-minute strategy call. We&rsquo;ll walk through your audit and show you exactly where your revenue is leaking &mdash; and how to fix it.
              </p>
              <a href="${CALENDLY_LINK}" style="display:inline-block; font-family:'Helvetica Neue',Arial,sans-serif; font-size:15px; color:${c.headerBg}; background:#ffffff; padding:14px 32px; border-radius:999px; text-decoration:none; font-weight:700; letter-spacing:-0.005em;">
                Book My Free Strategy Call &rarr;
              </a>
            </td>
          </tr>

          <!-- FOOTER -->
          <tr>
            <td style="background:#081420; padding:16px 40px; text-align:center;">
              <p style="margin:0; font-family:'Courier New',monospace; font-size:10px; letter-spacing:0.12em; color:#475569;">
                YourGrowthPartner.io &middot; Report generated for ${recipientEmail}
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

/* --------------------------------------------------------------------------
   Validation
   -------------------------------------------------------------------------- */

function normalizeUrl(input) {
  if (!input) return "";
  const trimmed = input.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function isValidUrl(input) {
  try {
    const u = new URL(normalizeUrl(input));
    return /\./.test(u.hostname) && u.hostname.length > 3;
  } catch {
    return false;
  }
}

function isValidEmail(input) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.trim());
}

function isValidPhone(input) {
  const digits = input.replace(/\D/g, "");
  return digits.length >= 6 && digits.length <= 15;
}

function getDomain(url) {
  try {
    return new URL(normalizeUrl(url)).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export default function GrowthAudit() {
  const [phase, setPhase] = useState("input");

  const [url, setUrl] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dialCode, setDialCode] = useState("+1");

  const [touched, setTouched] = useState({ url: false, email: false, phone: false });
  const [errors, setErrors] = useState({});
  const [errorMsg, setErrorMsg] = useState("");

  const [scanIndex, setScanIndex] = useState(0);
  const tickerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetch("https://ipapi.co/json/")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d && d.country_calling_code) setDialCode(d.country_calling_code);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => () => {
    if (tickerRef.current) clearInterval(tickerRef.current);
  }, []);

  function validate() {
    const next = {};
    if (!isValidUrl(url)) next.url = "Enter a full domain, including the dot.";
    if (!isValidEmail(email)) next.email = "We need a real address to send the audit.";
    if (!isValidPhone(phone)) next.phone = "Six to fifteen digits.";
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit() {
    setTouched({ url: true, email: true, phone: true });
    if (!validate()) return;

    setPhase("scanning");
    setScanIndex(0);
    setErrorMsg("");

    tickerRef.current = setInterval(() => {
      setScanIndex((i) => Math.min(i + 1, SCAN_LINES.length - 1));
    }, 1300);

    const finalUrl = normalizeUrl(url);
    const fullPhone = `${dialCode}${phone.replace(/\D/g, "")}`;
    const eventId = `lead_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

    try {
      const r = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: finalUrl, email, phone: fullPhone, eventId }),
      });

      clearInterval(tickerRef.current);

      if (r.status === 429) {
        setErrorMsg("Too many audits requested from your network in the last minute. Wait sixty seconds and try again.");
        setPhase("error");
        return;
      }

      if (!r.ok) {
        setErrorMsg("We couldn't complete the analysis. The site may be blocking automated visits, or the URL may be wrong.");
        setPhase("error");
        return;
      }

      const results = await r.json();

      fetch("https://api.emailjs.com/api/v1.0/email/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          service_id: EMAILJS_SERVICE_ID,
          template_id: EMAILJS_TEMPLATE_ID,
          user_id: EMAILJS_PUBLIC_KEY,
          template_params: {
            to_email: email,
            subject: `Growth Audit, ${getDomain(finalUrl)} (score ${results.overallScore} out of 100)`,
            html_body: buildEmailHTML(results, finalUrl, email),
          },
        }),
      }).catch(() => {});

      setScanIndex(SCAN_LINES.length - 1);
      window.fbq?.("track", "Lead", {}, { eventID: eventId });
      setTimeout(() => setPhase("success"), 380);
    } catch {
      clearInterval(tickerRef.current);
      setErrorMsg("We couldn't reach the analysis service. Try again in a moment.");
      setPhase("error");
    }
  }

  function reset() {
    if (tickerRef.current) clearInterval(tickerRef.current);
    setPhase("input");
    setUrl("");
    setEmail("");
    setPhone("");
    setTouched({ url: false, email: false, phone: false });
    setErrors({});
    setErrorMsg("");
    setScanIndex(0);
  }

  function blurField(name, validator) {
    setTouched((t) => ({ ...t, [name]: true }));
    setErrors((prev) => {
      const next = { ...prev };
      const value = name === "url" ? url : name === "email" ? email : phone;
      if (validator(value)) delete next[name];
      else next[name] = prev[name] || "Required.";
      return next;
    });
  }

  const formInvalid = !isValidUrl(url) || !isValidEmail(email) || !isValidPhone(phone);
  const domain = getDomain(url);

  return (
    <div className="page">
      <div className="masthead">
        <span className="wordmark">YourGrowthPartner</span>
        <span className="masthead-meta">Growth Audit &middot; Vol. 01</span>
      </div>

      <main>
        {phase === "input" && (
          <div className="shell phase">
            <section aria-labelledby="hero">
              <span className="tag">Free CRO &amp; growth audit</span>
              <h1 className="hero" id="hero">
                Paying for clicks but your landing page can't convert, know why.
              </h1>
              <p className="lede">
                Sixty seconds of your URL. We map the homepage against the CRE
                Methodology, find the single largest unaddressed objection, and
                email a verdict you can act on this afternoon.
              </p>
              <ul className="checklist">
                <li>Free to use</li>
                <li>No credit card</li>
                <li>Sixty-second turnaround</li>
              </ul>

            </section>

            <aside className="form-card" aria-labelledby="form-title">
              <h2 className="form-card-title" id="form-title">
                Where should we audit?
              </h2>
              <p className="form-card-sub">
                URL, email, phone. The audit lands in your inbox, the strategy call is up to you.
              </p>

              <form
                className="form"
                onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
                noValidate
              >
                <div className="field">
                  <label className="field-label" htmlFor="url-input">Website URL</label>
                  <input
                    id="url-input"
                    className={`field-input ${touched.url && errors.url ? "is-invalid" : ""}`}
                    type="url"
                    inputMode="url"
                    autoComplete="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    onBlur={() => blurField("url", isValidUrl)}
                    placeholder="yourdomain.com"
                    aria-invalid={touched.url && Boolean(errors.url)}
                    aria-describedby={errors.url ? "url-error" : undefined}
                    required
                  />
                  {touched.url && errors.url && (
                    <p className="field-error" id="url-error" role="alert">{errors.url}</p>
                  )}
                </div>

                <div className="field">
                  <label className="field-label" htmlFor="email-input">Email</label>
                  <input
                    id="email-input"
                    className={`field-input ${touched.email && errors.email ? "is-invalid" : ""}`}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => blurField("email", isValidEmail)}
                    placeholder="you@company.com"
                    aria-invalid={touched.email && Boolean(errors.email)}
                    aria-describedby={errors.email ? "email-error" : undefined}
                    required
                  />
                  {touched.email && errors.email && (
                    <p className="field-error" id="email-error" role="alert">{errors.email}</p>
                  )}
                </div>

                <div className="field">
                  <label className="field-label" htmlFor="phone-input">Phone</label>
                  <div className="field-phone">
                    <span className="dialcode" aria-label="Country dial code">{dialCode}</span>
                    <input
                      id="phone-input"
                      className={`field-input ${touched.phone && errors.phone ? "is-invalid" : ""}`}
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      onBlur={() => blurField("phone", isValidPhone)}
                      placeholder="555 0100"
                      aria-invalid={touched.phone && Boolean(errors.phone)}
                      aria-describedby={errors.phone ? "phone-error" : undefined}
                      required
                    />
                  </div>
                  {touched.phone && errors.phone && (
                    <p className="field-error" id="phone-error" role="alert">{errors.phone}</p>
                  )}
                </div>

                <button
                  type="submit"
                  className="cta"
                  disabled={formInvalid}
                  aria-disabled={formInvalid}
                >
                  Generate the audit
                  <span className="cta-glyph" aria-hidden="true">&rarr;</span>
                </button>

                <p className="fineprint">
                  Free. Sixty seconds. No card.
                </p>

                <p className="call-nudge-inline">
                  Already know you need help?{" "}
                  <a
                    href={CALENDLY_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Skip the audit. Book a strategy call directly &rarr;
                  </a>
                </p>
              </form>
            </aside>
          </div>
        )}

        {phase === "scanning" && (
          <section className="phase" aria-live="polite" aria-busy="true">
            <span className="tag">Auditing {domain || "your site"}</span>
            <h2 className="hero">
              Reading the page.
            </h2>
            <p className="lede">A few seconds. The audit is being composed.</p>

            <div className="scanner-wrap">
              <ol className="scanner" role="status">
                {SCAN_LINES.slice(0, scanIndex + 1).map((line, i) => {
                  const isCurrent = i === scanIndex;
                  const isDone = i < scanIndex;
                  return (
                    <li
                      key={i}
                      className={`scan-line ${isCurrent ? "is-current" : ""} ${isDone ? "is-done" : ""}`}
                    >
                      <span className="marker" aria-hidden="true">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text">{line}</span>
                    </li>
                  );
                })}
              </ol>
            </div>
          </section>
        )}

        {phase === "success" && (
          <section className="phase" aria-labelledby="confirm">
            <span className="tag">Audit composed</span>
            <h2 className="hero" id="confirm">
              Your audit is in transit to {email}.
            </h2>
            <p className="lede">
              If it doesn&apos;t arrive in five minutes, look in spam under
              YourGrowthPartner.io. A few inboxes filter the first delivery.
            </p>

            <div className="pullquote">
              <p className="pullquote-eyebrow">While the report lands</p>
              <p className="pullquote-line">
                Book a thirty-minute strategy call. We walk through your audit
                live and name the single fix worth the most revenue.
              </p>
              <a
                className="cta"
                href={CALENDLY_LINK}
                target="_blank"
                rel="noopener noreferrer"
                style={{ width: "auto", display: "inline-flex" }}
              >
                Book the strategy call
                <span className="cta-glyph" aria-hidden="true">&rarr;</span>
              </a>
            </div>

            <button className="secondary" type="button" onClick={reset}>
              Run another audit
            </button>
          </section>
        )}

        {phase === "error" && (
          <section className="phase error-block" aria-labelledby="err">
            <span className="tag" style={{ background: "oklch(86% 0.040 22)" }}>Audit failed</span>
            <h2 className="hero" id="err">
              Something didn&apos;t complete.
            </h2>
            <p className="lede">{errorMsg}</p>
            <button className="cta" type="button" onClick={reset} style={{ width: "auto", display: "inline-flex" }}>
              Try again
              <span className="cta-glyph" aria-hidden="true">&rarr;</span>
            </button>
          </section>
        )}
      </main>

      <footer className="colophon">
        <p>
          YourGrowthPartner.io. CRE Methodology and O/CO Analysis. Hand-tuned by a senior consultant.
        </p>
      </footer>
    </div>
  );
}
